import React, { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import useWindowWidth from "./useWindowWidth";
import ProtectedRoute from "./generalPages/ProtectedRoute";
import DashboardSidebar from "./Dashboard/DashboardSidebar";
import ConversationHistory from "./Dashboard/ConversationHistory";
import CallInfo from "./Dashboard/CallInfo";
import HIPAA from "./Dashboard/HIPAA";
import Help from "./Dashboard/Help";
import Agents from "./Dashboard/Agents";
import AgentInfoPage from "./Dashboard/AgentInfoPage";
import AgentConvoHistory from "./Dashboard/AgentConvoHistory";
import Account from "./Dashboard/MyAccount";
import Debug from "./Debug";
import LoginPage from "./generalPages/LoginPage";
import ForgotPasswordPage from "./generalPages/ForgotPassword";
import APIKeys from "./Dashboard/APIKeys";
import { WebsocketEvents, StreamType, FrontendScheduledCall, Call, WebsocketActions, Organization, WaitlistRun, Agent, AgentType, FrontendUser } from "./types";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "./store";
import { refreshAgentScheduledCallsPaginated } from "./slices/ScheduledCallSlice";
import { addCall, refreshAgentCallsPaginated } from "./slices/CallSlice";
import { getSession } from "./slices/AuthSlice";
import { usePostHog } from "posthog-js/react";
import Webhooks from "./Dashboard/Webhooks";
import Developers from "./Dashboard/Developers";
import WaitlistRunInfo from "./Dashboard/WaitlistRunInfo";
import WaitlistSlotInfo from "./Dashboard/WaitlistSlotInfo";
import { fetchWaitlistRun, fetchWaitlistRuns, setWaitlistRun } from "./slices/WaitlistRunsSlice";
import CalendlyIntegration from "./Dashboard/CalendlyIntegration";
import SelfAddForm from "./forms/SelfAddForm";
import Patients from "./Dashboard/Patients";
import { fetchWaitlistSlot, setWaitlistSlot } from "./slices/WaitlistSlotsSlice";
import PatientInfoPage from "./Dashboard/PatientInfoPage";
import Logs from "./Dashboard/Logs";
import Reporting from "./Dashboard/Reporting";
import PatientAvailability from "./generalPages/PatientAvailability";
import WaitlistRequests from "./Dashboard/WaitlistRequests";
import Todo from "./components/Todo";
import AdminPage from "./Dashboard/AdminPage";
import PayersPage from "./Dashboard/PayersPage";
import { PlanOfCareTemplateCreatePage, PlanOfCareTemplateViewPage } from "./Dashboard/PlanOfCarePage";
import AppointmentTypesPage from "./Dashboard/Settings/AppointmentTypes";
import NotificationsPage from "./Dashboard/NotificationsPage";
import CalendarPage from "./Dashboard/Calendar/Calendar";
import Settings from "./Dashboard/Settings/Settings";
import PaymentProcessing from "./Dashboard/Settings/PaymentProcessing";
import Reminders from "./Dashboard/Settings/Reminders";
import Cancellation from "./Dashboard/Settings/Cancellation";
import PaymentSuccess from "./generalPages/PaymentSuccess";
import { PlanOfCareActiveCreatePage, PlanOfCareActiveViewPage } from "./Dashboard/PatientPlanOfCarePage";
import { PlanOfCareDashboardPage } from "./Dashboard/PlanOfCareDashboardPage";
import Providers from "./Dashboard/Provider/Providers";
import AutoAdd from "./Dashboard/AutoAdd";
import { PlanOfCare } from "./Dashboard/Settings/PlanOfCare";
import Insurances from "./Dashboard/Settings/Insurances";
import BookInitialVisit from "./generalPages/BookInitialVisit";
import { ThemeProvider } from "./components/ui/theme-provider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import BookPlanOfCare from "./generalPages/BookPlanOfCare";
import { Toaster } from "./components/ui/sonner";
import Miscellaneous from "./Dashboard/Settings/Miscellaneous";
import AutoAddQueue from "./Dashboard/AutoAddQueue";
import AutoAddSettings from "./Dashboard/Settings/AutoAddSettings";
import Waitlist from "./Dashboard/Settings/Waitlist";
import AgentSettings from "./Dashboard/Settings/Agent";
import InitialVisit from "./Dashboard/Settings/InitialVisit";
import SuperAdmin from "./SuperAdmin";
import QuickAddPatientForm from "./Dashboard/QuickAddPatientForm";

const queryClient = new QueryClient();

function App() {
  const user: FrontendUser | null = useSelector((state: RootState) => state.auth.user);
  const authState = useSelector((state: RootState) => state.auth);
  const windowWidth = useWindowWidth();
  const minWidth = 200;
  const socketRef = useRef<WebSocket | null>(null);
  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const agents: Agent[] = useSelector((state: RootState) => state.agents.agents);
  const [firstWaitlistAgent, setFirstWaitlistAgent] = useState(agents.find((agent: Agent) => agent.agentType === AgentType.waitlist));

  useEffect(() => {
    if (agents) {
      setFirstWaitlistAgent(agents.find((agent: Agent) => agent.agentType === AgentType.waitlist));
    }
  }, [agents]);

  useEffect(() => {
    const awaitDispatch = async () => {
      if (!user || !user?.token) {
        return;
      }
      await dispatch(getSession());
    };

    awaitDispatch();
  }, [dispatch]);

  useEffect(() => {
    if (!user || !user?.userId || !user?.token) {
      return;
    }

    // Check if a WebSocket connection already exists
    if (socketRef.current && (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING)) {
      console.log("WebSocket connection is already open or connecting.");
      return;
    }

    const connectWebSocket = async () => {
      try {
        if (!user?.token) {
          return;
        }
        const socket = new WebSocket(`wss://${process.env.REACT_APP_BACKEND_URL_NO_HTTPS}/api/event-subscriptions/${user.userId}?token=${encodeURIComponent(user.token)}`);
        socketRef.current = socket;

        socket.onopen = async () => {
          // Add a delay to ensure the WebSocket connection is ready before subscribing
          // TODO (Hizami) - Figure out why the websocket connection is not immediately ready
          await new Promise((resolve) => setTimeout(resolve, 1000));
          console.log("Connected to WebSocket");
          socket.send(
            JSON.stringify({
              action: WebsocketActions.subscribe,
              type: WebsocketEvents.calls,
            }),
          );
          socket.send(
            JSON.stringify({
              action: WebsocketActions.subscribe,
              type: WebsocketEvents.scheduledCalls,
            }),
          );
          socket.send(
            JSON.stringify({
              action: WebsocketActions.subscribe,
              type: WebsocketEvents.waitlistRuns,
            }),
          );
          socket.send(
            JSON.stringify({
              action: WebsocketActions.subscribe,
              type: WebsocketEvents.waitlistSlotStateChanges,
            }),
          );
          socket.send(
            JSON.stringify({
              action: WebsocketActions.subscribe,
              type: WebsocketEvents.waitlistSlots,
            }),
          );
          socket.send(
            JSON.stringify({
              action: WebsocketActions.subscribe,
              type: WebsocketEvents.waitlistPatients,
            }),
          );
          socket.send(
            JSON.stringify({
              action: WebsocketActions.subscribe,
              type: WebsocketEvents.patients,
            }),
          );
        };

        socket.onmessage = (event: MessageEvent<string>) => {
          const message: {
            eventType: WebsocketEvents;
            streamType: StreamType;
            data: Call | FrontendScheduledCall | any;
          } = JSON.parse(event.data) as {
            eventType: WebsocketEvents;
            streamType: StreamType;
            data: Call | FrontendScheduledCall | any;
          };

          switch (message.eventType) {
            case WebsocketEvents.calls:
              switch (message.streamType) {
                case StreamType.INSERT: {
                  const call: Call = message.data as Call;
                  dispatch(addCall({ call: call }));
                  if (!user?.token) {
                    return;
                  }
                  dispatch(
                    refreshAgentCallsPaginated({
                      token: user.token,
                      agentId: call.agentId,
                    }),
                  ); // Refresh the scheduled calls list
                  break;
                }
                case StreamType.REMOVE: {
                  // const callId: string = (message.data as FrontendCall).callId;
                  // dispatch(deleteCall({ callId: callId }));
                  break;
                }
                case StreamType.MODIFY: {
                  //const callModifyCase: FrontendCall = message.data;
                  //const { callId: callIdModifyCase, ...rest } = callModifyCase;
                  // dispatch(modifyCall({ callId: callIdModifyCase, changes: rest }));
                  break;
                }
              }
              break;
            case WebsocketEvents.scheduledCalls:
              switch (message.streamType) {
                case StreamType.INSERT: {
                  const scheduledCallInsertCase: FrontendScheduledCall = message.data as FrontendScheduledCall;
                  if (!user?.token) {
                    return;
                  }
                  dispatch(
                    refreshAgentScheduledCallsPaginated({
                      token: user.token,
                      agentId: scheduledCallInsertCase.agentId,
                    }),
                  );
                  break;
                }
                case StreamType.REMOVE: {
                  const scheduledCallRemoveCase: FrontendScheduledCall = message.data as FrontendScheduledCall;
                  if (!user?.token) {
                    return;
                  }
                  dispatch(
                    refreshAgentScheduledCallsPaginated({
                      token: user.token,
                      agentId: scheduledCallRemoveCase.agentId,
                    }),
                  );
                  break;
                }
                case StreamType.MODIFY: {
                  const scheduledCallModifyCase: FrontendScheduledCall = message.data as FrontendScheduledCall;
                  if (!user?.token) {
                    return;
                  }
                  dispatch(
                    refreshAgentScheduledCallsPaginated({
                      token: user.token,
                      agentId: scheduledCallModifyCase.agentId,
                    }),
                  );
                  break;
                }
              }
              break;
            case WebsocketEvents.waitlistRuns:
              switch (message.streamType) {
                case StreamType.INSERT: {
                  console.log("Received waitlist run insert message:", message);
                  if (!user?.token) {
                    return;
                  }
                  const waitlistRun: WaitlistRun = message.data as WaitlistRun;
                  dispatch(
                    fetchWaitlistRun({
                      token: user.token,
                      waitlistRunId: waitlistRun.waitlistRunId,
                    }),
                  );
                  break;
                }
                case StreamType.REMOVE: {
                  console.log("Received waitlist run remove message:", message);
                  if (!user?.token) {
                    return;
                  }
                  const waitlistRun: WaitlistRun = message.data as WaitlistRun;
                  dispatch(
                    setWaitlistRun({
                      waitlistRunId: waitlistRun.waitlistRunId,
                      waitlistRun: undefined,
                    }),
                  );
                  break;
                }
                case StreamType.MODIFY: {
                  console.log("Received waitlist run modify message:", message);
                  if (!user?.token) {
                    return;
                  }
                  const waitlistRun: WaitlistRun = message.data as WaitlistRun;
                  dispatch(
                    fetchWaitlistRun({
                      token: user.token,
                      waitlistRunId: waitlistRun.waitlistRunId,
                    }),
                  );
                  break;
                }
              }
              break;
            default:
              console.log("Received unknown message:", message);
              break;

            case WebsocketEvents.waitlistSlotStateChanges:
              if (!user?.token) {
                return;
              }
              switch (message.streamType) {
                case StreamType.INSERT: {
                  if (firstWaitlistAgent) {
                    dispatch(
                      fetchWaitlistRuns({
                        token: user.token,
                        agentId: firstWaitlistAgent.agentId,
                      }),
                    );
                  }
                  break;
                }
                case StreamType.REMOVE: {
                  if (firstWaitlistAgent) {
                    dispatch(
                      fetchWaitlistRuns({
                        token: user.token,
                        agentId: firstWaitlistAgent.agentId,
                      }),
                    );
                  }
                  break;
                }
                case StreamType.MODIFY: {
                  if (firstWaitlistAgent) {
                    dispatch(
                      fetchWaitlistRuns({
                        token: user.token,
                        agentId: firstWaitlistAgent.agentId,
                      }),
                    );
                  }
                  break;
                }
              }
              break;
            case WebsocketEvents.waitlistSlots:
              if (!user?.token) {
                return;
              }
              switch (message.streamType) {
                case StreamType.INSERT: {
                  dispatch(
                    fetchWaitlistSlot({
                      token: user.token,
                      waitlistSlotId: message.data.waitlistSlotId,
                    }),
                  );
                  break;
                }
                case StreamType.REMOVE: {
                  dispatch(
                    setWaitlistSlot({
                      waitlistSlotId: message.data.waitlistSlotId,
                      waitlistSlot: undefined,
                    }),
                  );
                  break;
                }
                case StreamType.MODIFY: {
                  dispatch(
                    fetchWaitlistSlot({
                      token: user.token,
                      waitlistSlotId: message.data.waitlistSlotId,
                    }),
                  );
                  break;
                }
              }
              break;
            case WebsocketEvents.waitlistPatients:
              if (!user?.token) {
                return;
              }
              switch (message.streamType) {
                case StreamType.INSERT: {
                  if (firstWaitlistAgent) {
                    dispatch(
                      fetchWaitlistRuns({
                        token: user.token,
                        agentId: firstWaitlistAgent.agentId,
                      }),
                    );
                  }
                  break;
                }
                case StreamType.REMOVE: {
                  if (firstWaitlistAgent) {
                    dispatch(
                      fetchWaitlistRuns({
                        token: user.token,
                        agentId: firstWaitlistAgent.agentId,
                      }),
                    );
                  }
                  break;
                }
                case StreamType.MODIFY: {
                  if (firstWaitlistAgent) {
                    dispatch(
                      fetchWaitlistRuns({
                        token: user.token,
                        agentId: firstWaitlistAgent.agentId,
                      }),
                    );
                  }
                  break;
                }
              }
              break;
            case WebsocketEvents.patients:
              if (!user?.token) {
                return;
              }
              switch (message.streamType) {
                case StreamType.INSERT: {
                  if (firstWaitlistAgent) {
                    dispatch(
                      fetchWaitlistRuns({
                        token: user.token,
                        agentId: firstWaitlistAgent.agentId,
                      }),
                    );
                  }
                  break;
                }
                case StreamType.REMOVE: {
                  if (firstWaitlistAgent) {
                    dispatch(
                      fetchWaitlistRuns({
                        token: user.token,
                        agentId: firstWaitlistAgent.agentId,
                      }),
                    );
                  }
                  break;
                }
                case StreamType.MODIFY: {
                  if (firstWaitlistAgent) {
                    dispatch(
                      fetchWaitlistRuns({
                        token: user.token,
                        agentId: firstWaitlistAgent.agentId,
                      }),
                    );
                  }
                  break;
                }
              }
              break;
          }
        };

        socket.onclose = () => {
          console.log("Disconnected from WebSocket");
          socketRef.current = null;
        };

        socket.onerror = (error) => {
          console.error("WebSocket error:", error);
          socket.close();
        };

        return () => {
          socket.close();
        };
      } catch (error) {
        console.error("Error connecting to WebSocket:", error);
      }
    };

    connectWebSocket();

    // Clean up the WebSocket connection when the component unmounts or user changes
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [user?.token]);

  useEffect(() => {
    if (!user || !user?.userId) {
      return;
    }
    posthog?.identify(user.userId);
    posthog?.people.set({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      organizationId: user.organizationId,
    });
    posthog?.capture("Logged in", { email: user.email });
  }, [user]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="react-ui-theme">
        {windowWidth >= minWidth ? (
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            {/* <Route path="/walkthrough/create-account" element={<SignupWalkthrough />} /> */}

            <Route path="/dashboard" element={<ProtectedRoute element={<DashboardSidebar />} />}>
              <Route index element={<Navigate to="/dashboard/agents" replace />} />
              <Route path="/dashboard/conversation-history" element={<ConversationHistory />} />
              <Route path="/dashboard/conversation-history/calls/:callId" element={<CallInfo />} />
              <Route path="/dashboard/agents" element={<Agents />} />
              <Route path="/dashboard/agents/:agentId" element={<AgentInfoPage />} />
              <Route path="/dashboard/agents/:agentId/agent-conversation-history" element={<AgentConvoHistory />} />
              <Route path="/dashboard/agents/:agentId/agent-conversation-history/calls/:callId" element={<CallInfo />} />
              <Route path="/dashboard/agents/:agentId/waitlist-runs/:waitlistRunId" element={<WaitlistRunInfo />} />
              <Route path="/dashboard/agents/:agentId/waitlist-runs/:waitlistRunId/slots/:waitlistSlotId" element={<WaitlistSlotInfo />} />
              <Route path="/dashboard/patients/auto-add" element={<AutoAdd />} />
              <Route path="/dashboard/patients/auto-add-queue" element={<AutoAddQueue />} />
              <Route path="/dashboard/patients" element={<Patients />} />
              <Route path="/dashboard/patients/:patientId" element={<PatientInfoPage />} />
              <Route path="/dashboard/patients/:patientId/plan-of-care" element={<PlanOfCareActiveCreatePage />} />
              <Route path="/dashboard/patients/:patientId/plan-of-care/:planOfCareId" element={<PlanOfCareActiveViewPage />} />
              <Route path="/dashboard/hipaa" element={<HIPAA />} />
              <Route path="/dashboard/help" element={<Help />} />
              <Route path="/dashboard/account" element={<Account />} />
              <Route path="/dashboard/developers" element={<Developers />} />
              <Route path="/dashboard/keys" element={<APIKeys />} />
              <Route path="/dashboard/webhooks" element={<Webhooks />} />
              <Route path="/dashboard/logs" element={<Logs />} />
              <Route path="/dashboard/reporting" element={<Reporting />} />
              <Route path="/dashboard/todo" element={<Todo />} />
              <Route path="/dashboard/waitlist-requests" element={<WaitlistRequests />} />
              <Route path="/dashboard/admin" element={<AdminPage />} />
              <Route path="/dashboard/payers" element={<PayersPage />} />
              <Route path="/dashboard/plan-of-care" element={<PlanOfCareDashboardPage />} />
              <Route path="/dashboard/plan-of-care/create" element={<PlanOfCareTemplateCreatePage />} />
              <Route path="/dashboard/plan-of-care/view" element={<PlanOfCareTemplateViewPage />} />
              <Route path="/dashboard/patient-plan-of-care/view" element={<PlanOfCareActiveCreatePage />} />
              <Route path="/dashboard/notifications" element={<NotificationsPage />} />
              {/* <Route path="/dashboard/calendar" element={<CalendarPage />} /> */}
              <Route path="/dashboard/settings" element={<Settings />} />
              <Route path="/dashboard/settings/agent" element={<AgentSettings />} />
              <Route path="/dashboard/settings/insurances" element={<Insurances />} />
              <Route path="/dashboard/settings/payment-processing" element={<PaymentProcessing />} />
              <Route path="/dashboard/settings/miscellaneous" element={<Miscellaneous />} />
              <Route path="/dashboard/settings/appointment-types" element={<AppointmentTypesPage />} />
              <Route path="/dashboard/settings/auto-add" element={<AutoAddSettings />} />
              <Route path="/dashboard/settings/functional-modules" element={<PlanOfCare />} />
              <Route path="/dashboard/settings/functional-modules/waitlist" element={<Waitlist />} />
              <Route path="/dashboard/settings/functional-modules/initial-visit" element={<InitialVisit />} />
              <Route path="/dashboard/settings/functional-modules/reminders" element={<Reminders />} />
              <Route path="/dashboard/settings/functional-modules/cancellation" element={<Cancellation />} />
              <Route path="/dashboard/providers" element={<Providers />} />
              <Route path="/dashboard/providers/groups" element={<Navigate to="/dashboard/providers" replace />} />
              <Route path="/dashboard/providers/add" element={<Navigate to="/dashboard/providers" replace />} />
            </Route>
            <Route path="/patient/self-add/:orgId" element={<SelfAddForm />} />
            <Route path="/patient/availability/:uniqueId" element={<PatientAvailability />} />
            <Route path="/patient/book-plan-of-care-visit/:uniqueId" element={<BookPlanOfCare />} />
            <Route path="/patient/book-initial-visit/:uniqueId" element={<BookInitialVisit />} />
            <Route path="/patient/self-enroll/:formId" element={<QuickAddPatientForm />} />
            <Route path="/calendly/auth" element={<CalendlyIntegration />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            {process.env.NODE_ENV === "development" && <Route path="/debug" element={<Debug />} />}
            <Route path="/super-admin" element={<SuperAdmin />} />
          </Routes>
        ) : (
          <div style={{ width: "70%", margin: "100px auto", fontSize: "2rem" }}>The screen is too small to display this application. Please use a larger screen.</div>
        )}
        <Toaster richColors />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
